import React, { FC, memo, useEffect, useState } from 'react'

import { useFocusNodeById, useSetFocus } from '@please/lrud'
import { useLocation } from 'react-router-dom'

import { focusIds } from '@/constants/focus'
import { paths } from '@/constants/paths'
import useStore from '@/store/useStore'
import { isPointerMode } from '@/utils/utils'

import { useMainMenuItems } from './hooks'
import { MainMenu } from './MainMenu'
import { SidebarElement } from './styles'
import { SubMenu } from './SubMenu'

type Props = {
  focusIdPrefix?: string
  disableSubMenuAutoOpen?: boolean
  onBack?: () => void
}

const MainSidebarRaw: FC<Props> = ({
  focusIdPrefix = '',
  disableSubMenuAutoOpen,
  onBack,
}) => {
  const { pathname } = useLocation()
  const setFocus = useSetFocus()

  const subMenuType = useStore((state) => state.common.subMenuType)
  const setSubMenuType = useStore((state) => state.common.setSubMenuType)
  const appSettings = useStore((state) => state.common.appSettings)
  const isFullscreen = useStore((state) => state.player.isFullscreen)

  const [isSidebarFocused, setSidebarFocused] = useState(false)

  const { sportsMenuItems } = useMainMenuItems()

  const isHidden =
    pathname === paths.LOGIN ||
    pathname === paths.INDEX ||
    pathname === paths.ROOT ||
    isFullscreen

  const onMenuFocused = () => setSidebarFocused(true)
  const onMenuBlurred = () => setSidebarFocused(false)

  const backToMainMenu = () => {
    setSubMenuType(null)
    if (!subMenuType) {
      return
    }

    setTimeout(() => setFocus(focusIdPrefix + subMenuType), 10)
  }
  const openSportsSubMenu = () => setSubMenuType(focusIds.CATALOGUE)

  const focusNode = useFocusNodeById(focusIdPrefix + focusIds.SIDEBAR)

  useEffect(() => {
    const sidebarNode = focusNode?.elRef?.current
    if (!isPointerMode || !sidebarNode) {
      return
    }
    sidebarNode.addEventListener('click', onClickHandler)
    return () => {
      sidebarNode.removeEventListener('click', onClickHandler)
    }
  }, [focusNode])

  const onClickHandler = (e: MouseEvent) => {
    if (!isSidebarFocused) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const props = {
    focusIdPrefix,
    disableSubMenuAutoOpen,
    isSidebarFocused,
    onMenuFocused,
    onMenuBlurred,
  }

  return (
    <SidebarElement
      elementType="aside"
      focusId={focusIdPrefix + focusIds.SIDEBAR}
      $isOpen={isSidebarFocused}
      $isVisible={!isHidden}
      disabled={isHidden}
      $isBlurUsed={!appSettings.isDisabledBlur}
      onMouseEnter={onMenuFocused}
      onMouseLeave={onMenuBlurred}
    >
      <MainMenu {...props} isOpen={!subMenuType} onBack={onBack} />
      <SubMenu
        {...props}
        menuItems={sportsMenuItems}
        isOpen={subMenuType === focusIds.CATALOGUE}
        path={paths.PAGE}
        backToMainMenu={backToMainMenu}
        openSubMenu={openSportsSubMenu}
      />
    </SidebarElement>
  )
}

export const MainSidebar = memo(MainSidebarRaw)
